import { useState, useRef, useEffect } from 'react';
import { NJIconButton } from '@engie-group/fluid-design-system-react';
import type { VariableType } from './types';

interface ToolbarProps {
  nodeVariable: VariableType;
  pipeVariable: VariableType;
  nodesVisible: boolean;
  pipesVisible: boolean;
  onNodeVariableChange: (v: VariableType) => void;
  onPipeVariableChange: (v: VariableType) => void;
  onNodesVisibleChange: (v: boolean) => void;
  onPipesVisibleChange: (v: boolean) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
}

type OpenMenu = 'nodes' | 'pipes' | 'altitudes' | 'options' | null;

export default function Toolbar({
  nodeVariable,
  pipeVariable,
  nodesVisible,
  pipesVisible,
  onNodeVariableChange,
  onPipeVariableChange,
  onNodesVisibleChange,
  onPipesVisibleChange,
  searchQuery,
  onSearchChange,
}: ToolbarProps) {
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  return (
    <div
      id="editor-toolbar"
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, background: 'var(--nj-semantic-color-background-neutral-primary-default)', borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', position: 'relative' }}
      ref={menuRef}
    >
      {/* Nœuds */}
      <div style={{ position: 'relative' }}>
        <button type="button" className="nj-btn nj-btn--secondary nj-btn--subtle" style={{ padding: 8 }} onClick={() => setOpenMenu(openMenu === 'nodes' ? null : 'nodes')}>
          <span className="material-icons nj-icon-material nj-btn__icon" aria-hidden="true">keyboard_arrow_down</span>
          Nœuds
        </button>
        {openMenu === 'nodes' && (
          <NodeMenu
            variable={nodeVariable}
            visible={nodesVisible}
            onVariableChange={onNodeVariableChange}
            onVisibilityChange={onNodesVisibleChange}
          />
        )}
      </div>

      {/* Canalisations */}
      <div style={{ position: 'relative' }}>
        <button type="button" className="nj-btn nj-btn--secondary nj-btn--subtle" style={{ padding: 8 }} onClick={() => setOpenMenu(openMenu === 'pipes' ? null : 'pipes')}>
          <span className="material-icons nj-icon-material nj-btn__icon" aria-hidden="true">keyboard_arrow_down</span>
          Canalisations
        </button>
        {openMenu === 'pipes' && (
          <PipeMenu
            variable={pipeVariable}
            visible={pipesVisible}
            onVariableChange={onPipeVariableChange}
            onVisibilityChange={onPipesVisibleChange}
          />
        )}
      </div>

      {/* Altitudes */}
      <button type="button" className="nj-btn nj-btn--secondary nj-btn--subtle" style={{ padding: 8 }} onClick={() => setOpenMenu(openMenu === 'altitudes' ? null : 'altitudes')}>
        <span className="material-icons nj-icon-material nj-btn__icon" aria-hidden="true">keyboard_arrow_down</span>
        Altitudes
      </button>

      {/* Options */}
      <button type="button" className="nj-btn nj-btn--secondary nj-btn--subtle" style={{ padding: 8 }} onClick={() => setOpenMenu(openMenu === 'options' ? null : 'options')}>
        <span className="material-icons nj-icon-material nj-btn__icon" aria-hidden="true">keyboard_arrow_down</span>
        Options
      </button>

      {/* Search form */}
      <form
        id="entity-find-form"
        style={{ display: 'flex', alignItems: 'flex-end', gap: 4, marginLeft: 'auto' }}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="nj-form-item">
          <div className="nj-form-item__field-wrapper">
            <input
              id="entity-name-input"
              type="text"
              placeholder=" "
              className="nj-form-item__field"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            <label htmlFor="entity-name-input" className="nj-form-item__label">
              Nom de l&apos;entité
            </label>
          </div>
        </div>
        <NJIconButton icon="search" label="Rechercher" scale="lg" />
      </form>

      <NJIconButton icon="adjust" label="Mettre à jour les diamètres" variant="tertiary" scale="sm" />
      <NJIconButton icon="fullscreen" label="Entrer en plein écran" variant="tertiary" scale="sm" />
    </div>
  );
}

const NODE_VARIABLES = [
  { value: 'pressure_bar', label: 'Pression [bar]' },
  { value: 'temperature_c', label: 'Température [°C]' },
  { value: 'pressure_without_altitude_bar', label: 'Pression sans altitude [bar]' },
  { value: 'altitude', label: 'Altitude [m]' },
  { value: 'delta_p', label: 'DeltaP [bar]' },
  { value: 'delta_t', label: 'DeltaT [Δ°C]' },
  { value: 'temperature_retour', label: 'Température retour [°C]' },
  { value: 'debit_massique', label: 'Débit massique [T/h]' },
  { value: 'debit_volumique', label: 'Débit volumique livré [m³/h]' },
  { value: 'puissance_appelee', label: 'Puissance appelée [kW]' },
  { value: 'puissance_perte_charge', label: 'Puissance perdue par perte de charge [kW]' },
  { value: 'puissance_appelee_corrigee', label: 'Puissance appelée corrigée [kW]' },
  { value: 'puissance_delivree', label: 'Puissance délivrée [kW]' },
  { value: 'puissance_non_livree', label: 'Puissance non-livrée [kW]' },
  { value: 'delta_p_vanne', label: 'DeltaP vanne [bar]' },
  { value: 'puissance_produite', label: 'Puissance produite [MW]' },
  { value: 'delta_p_prod', label: 'Delta P [bar]' },
  { value: 'delta_t_prod', label: 'Delta T [Δ°C]' },
  { value: 'debit_m3h', label: 'Débit [m³/h]' },
  { value: 'temperature_souhaitee', label: 'Température souhaitée [°C]' },
  { value: 'slack_temperature', label: 'Slack de température [Δ°C]' },
  { value: 'debit_vol_primaire', label: 'Débit volumique primaire [m³/h]' },
  { value: 'debit_mass_primaire', label: 'Débit massique primaire [T/h]' },
  { value: 'pression_entree_primaire', label: 'Pression entrée primaire [bar]' },
  { value: 'delta_p_primaire', label: 'DeltaP primaire [bar]' },
  { value: 'temp_entree_primaire', label: 'Temperature entrée primaire [°C]' },
  { value: 'delta_t_primaire', label: 'DeltaT primaire [Δ°C]' },
  { value: 'debit_vol_secondaire', label: 'Débit volumique secondaire [m³/h]' },
  { value: 'debit_mass_secondaire', label: 'Débit massique secondaire [T/h]' },
  { value: 'temp_entree_secondaire', label: 'Temperature entrée secondaire [°C]' },
  { value: 'delta_t_secondaire', label: 'DeltaT secondaire [Δ°C]' },
  { value: 'pression_entree_secondaire', label: 'Pression entrée secondaire [bar]' },
  { value: 'delta_p_secondaire', label: 'DeltaP secondaire [bar]' },
  { value: 'altitude_sst', label: "Altitude sous-station d'échange [m]" },
  { value: 'zone_influence', label: "Zone d'influence" },
];

const PIPE_VARIABLES = [
  { value: 'pressure_bar', label: 'Pression moyenne [bar]' },
  { value: 'temperature_c', label: 'Température moyenne [°C]' },
  { value: 'pressure_without_altitude_bar', label: 'Pression sans altitude [bar]' },
  { value: 'speed_flow', label: 'Vitesse [m/s]' },
  { value: 'net_flow', label: 'Débit massique [T/h]' },
  { value: 'net_volumetric_flow', label: 'Débit volumique [m³/h]' },
  { value: 'delta_pressure_bar', label: 'DeltaP [bar]' },
  { value: 'gradient_loss', label: 'Gradient de perte [Pa/m]' },
  { value: 'nominal_diameter', label: 'Diamètre nominal [m]' },
  { value: 'available_flow', label: 'Débit disponible [T/h]' },
  { value: 'available_power', label: 'Puissance disponible [kW]' },
  { value: 'weighted_deltaT', label: 'DeltaT pondéré [Δ°C]' },
  { value: 'available_flow_overall', label: 'Débit disponible global [T/h]' },
  { value: 'available_power_overall', label: 'Puissance disponible globale [kW]' },
  { value: 'global_optimized_diameter', label: 'Diamètre optimisé global [m]' },
  { value: 'step_optimized_diameter', label: 'Diamètre optimisé par pas [m]' },
];

function NodeMenu({ variable, visible, onVariableChange, onVisibilityChange }: {
  variable: VariableType;
  visible: boolean;
  onVariableChange: (v: VariableType) => void;
  onVisibilityChange: (v: boolean) => void;
}) {
  const currentLabel = NODE_VARIABLES.find((v) => v.value === variable)?.label || 'Pression [bar]';
  const [selectOpen, setSelectOpen] = useState(false);

  return (
    <div className="nj-menu" role="menu" style={menuStyle}>
      <form style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 8 }}>
        {/* Variable FDS select */}
        <div className="nj-form-item nj-form-item--select nj-form-item--custom-list" style={{ position: 'relative' }}>
          <div className="nj-form-item__field-wrapper" onClick={() => setSelectOpen(!selectOpen)} style={{ cursor: 'pointer' }}>
            <input
              type="text"
              placeholder=" "
              className="nj-form-item__field"
              readOnly
              value={currentLabel}
              style={{ cursor: 'pointer', pointerEvents: 'none' }}
            />
            <label className="nj-form-item__label">Variable</label>
            <span className="material-icons nj-icon-material nj-form-item__icon" aria-hidden="true" style={{ cursor: 'pointer', pointerEvents: 'none' }}>keyboard_arrow_down</span>
          </div>
          {selectOpen && (
            <div className="nj-list" role="listbox" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 10, background: '#fff', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, maxHeight: 200, overflowY: 'auto', padding: 0, margin: '4px 0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: 'max-content', minWidth: '100%' }}>
              {NODE_VARIABLES.map((v) => (
                <button key={v.value} type="button" className="nj-list__item-wrapper" role="option" aria-selected={v.value === variable} style={{ width: '100%', background: v.value === variable ? 'var(--nj-core-color-blue-alpha-200)' : 'transparent' }} onClick={() => { onVariableChange(v.value as VariableType); setSelectOpen(false); }}>
                  <span className="nj-list__item-content"><span className="nj-list__item-primary">{v.label}</span></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Visibility checkbox */}
        <div className="nj-checkbox">
          <label htmlFor="node-visibility">
            <input type="checkbox" id="node-visibility" checked={visible} onChange={(e) => onVisibilityChange(e.target.checked)} />
            <span className="nj-checkbox__label">Visibilité</span>
          </label>
        </div>

        {/* Color scale visibility */}
        <div className="nj-checkbox">
          <label htmlFor="node-cs-visibility">
            <input type="checkbox" id="node-cs-visibility" defaultChecked />
            <span className="nj-checkbox__label">Visibilité de l&apos;échelle de couleur</span>
          </label>
        </div>

        {/* Log scale */}
        <div className="nj-checkbox">
          <label htmlFor="node-log-scale">
            <input type="checkbox" id="node-log-scale" />
            <span className="nj-checkbox__label">Échelle logarithmique</span>
          </label>
        </div>

        {/* Steps */}
        <div className="nj-form-item">
          <div className="nj-form-item__field-wrapper">
            <input id="node-steps" type="number" placeholder=" " className="nj-form-item__field" defaultValue={10} />
            <label htmlFor="node-steps" className="nj-form-item__label">Nombre de pas</label>
          </div>
        </div>

        {/* Color pickers */}
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="color" defaultValue="#FF0000" style={{ width: 32, height: 32, border: 'none', cursor: 'pointer' }} />
          <input type="color" defaultValue="#00FF00" style={{ width: 32, height: 32, border: 'none', cursor: 'pointer' }} />
          <input type="color" defaultValue="#0000FF" style={{ width: 32, height: 32, border: 'none', cursor: 'pointer' }} />
        </div>
      </form>
    </div>
  );
}

function PipeMenu({ variable, visible, onVariableChange, onVisibilityChange }: {
  variable: VariableType;
  visible: boolean;
  onVariableChange: (v: VariableType) => void;
  onVisibilityChange: (v: boolean) => void;
}) {
  const currentLabel = PIPE_VARIABLES.find((v) => v.value === variable)?.label || 'Pression moyenne [bar]';
  const [selectOpen, setSelectOpen] = useState(false);

  return (
    <div className="nj-menu" role="menu" style={menuStyle}>
      <form style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 8 }}>
        {/* Variable FDS select */}
        <div className="nj-form-item nj-form-item--select nj-form-item--custom-list" style={{ position: 'relative' }}>
          <div className="nj-form-item__field-wrapper" onClick={() => setSelectOpen(!selectOpen)} style={{ cursor: 'pointer' }}>
            <input type="text" placeholder=" " className="nj-form-item__field" readOnly value={currentLabel} style={{ cursor: 'pointer', pointerEvents: 'none' }} />
            <label className="nj-form-item__label">Variable</label>
            <span className="material-icons nj-icon-material nj-form-item__icon" aria-hidden="true" style={{ cursor: 'pointer', pointerEvents: 'none' }}>keyboard_arrow_down</span>
          </div>
          {selectOpen && (
            <div className="nj-list" role="listbox" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 10, background: '#fff', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, maxHeight: 200, overflowY: 'auto', padding: 0, margin: '4px 0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: 'max-content', minWidth: '100%' }}>
              {PIPE_VARIABLES.map((v) => (
                <button key={v.value} type="button" className="nj-list__item-wrapper" role="option" aria-selected={v.value === variable} style={{ width: '100%', background: v.value === variable ? 'var(--nj-core-color-blue-alpha-200)' : 'transparent' }} onClick={() => { onVariableChange(v.value as VariableType); setSelectOpen(false); }}>
                  <span className="nj-list__item-content"><span className="nj-list__item-primary">{v.label}</span></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Visibility checkboxes */}
        <div className="nj-checkbox">
          <label htmlFor="pipe-outward-visibility">
            <input type="checkbox" id="pipe-outward-visibility" checked={visible} onChange={(e) => onVisibilityChange(e.target.checked)} />
            <span className="nj-checkbox__label">Visibilité aller</span>
          </label>
        </div>
        <div className="nj-checkbox">
          <label htmlFor="pipe-return-visibility">
            <input type="checkbox" id="pipe-return-visibility" defaultChecked />
            <span className="nj-checkbox__label">Visibilité retour</span>
          </label>
        </div>
        <div className="nj-checkbox">
          <label htmlFor="pipe-cs-visibility">
            <input type="checkbox" id="pipe-cs-visibility" defaultChecked />
            <span className="nj-checkbox__label">Visibilité de l&apos;échelle de couleur</span>
          </label>
        </div>
        <div className="nj-checkbox">
          <label htmlFor="pipe-arrows">
            <input type="checkbox" id="pipe-arrows" />
            <span className="nj-checkbox__label">Flèches de direction</span>
          </label>
        </div>
        <div className="nj-checkbox">
          <label htmlFor="pipe-log-scale">
            <input type="checkbox" id="pipe-log-scale" />
            <span className="nj-checkbox__label">Échelle logarithmique</span>
          </label>
        </div>

        {/* Steps */}
        <div className="nj-form-item">
          <div className="nj-form-item__field-wrapper">
            <input id="pipe-steps" type="number" placeholder=" " className="nj-form-item__field" defaultValue={10} />
            <label htmlFor="pipe-steps" className="nj-form-item__label">Nombre de pas</label>
          </div>
        </div>

        {/* Color pickers */}
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="color" defaultValue="#FF0000" style={{ width: 32, height: 32, border: 'none', cursor: 'pointer' }} />
          <input type="color" defaultValue="#00FF00" style={{ width: 32, height: 32, border: 'none', cursor: 'pointer' }} />
          <input type="color" defaultValue="#0000FF" style={{ width: 32, height: 32, border: 'none', cursor: 'pointer' }} />
        </div>
      </form>
    </div>
  );
}

const menuStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 1000,
  background: 'var(--nj-semantic-color-background-neutral-primary-default)',
  border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
  borderRadius: 4,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  minWidth: 220,
  width: 'max-content',
  padding: 8,
};
